import React, {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import SEO from '../../components/seo';
import LayoutOne from '../../layouts/LayoutOne';
import Breadcrumb from '../../wrappers/breadcrumb/Breadcrumb';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation} from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  authSelector,
  getAgentOrders,
  getPerformSearch,
} from '../../store/slices/auth-slice';
import Table from '../../components/Table';
import { formatDate } from '../../helpers/common';

const Orders = () => {
  const {t, i18n} = useTranslation();
  let { pathname } = useLocation();
  const { isLoading } = useSelector(authSelector);

  const dispatch = useDispatch();
  const [agentOrders, setAgentOrders] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [sortData, setSortData] = useState('');

  const pageSize = 10;

  useEffect(() => {
    fetchAgentOrders(currentIndex, pageSize, sortData, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortData, appliedFilters, currentIndex]);

  useEffect(() => {
    dispatch(
      getPerformSearch({
        setPerformSearch: setOrderStatus,
        entityName: 'StatusItem',
        inputFields: {
          statusTypeId: 'ORDER_STATUS',
        },
        noConditionFind: 'N',
      })
    );
  }, []);

  const fetchAgentOrders = async (
    viewIndex = 1,
    viewSize = 10,
    sortField = '',
    filters = []
  ) => {
    let appliedFilters = {};

    if (filters?.length > 0) {
      filters?.forEach((filter) => {
        if (filter?.id === 'orderEntryDates') {
          appliedFilters[filter.id] = {
            startDate: formatDate(
              new Date(filter?.value?.startDate),
              'YYYY-MM-DD HH:mm:ss.SSS'
            ),
            endDate: formatDate(
              new Date(filter?.value?.endDate),
              'YYYY-MM-DD HH:mm:ss.SSS'
            ),
          };
        } else {
          appliedFilters[filter.id] = filter?.value || '';
        }
      });
    }
    dispatch(
      getAgentOrders({
        viewIndex: viewIndex - 1,
        viewSize,
        sortField,
        appliedFilters,
        setOrganizations,
        setAgentOrders,
        setTotalRecords,
      })
    );
  };

  const handleServerSideSortChange = (newSorting) => {
    setSortData(newSorting);
  };

  // Similarly, define how to handle server‐side filter changes
  const handleServerSideFilterChange = (newFilters) => {
    setAppliedFilters(newFilters);
  };

  // If user changes the page (paginator, sorting, filtering, etc.),
  // we can re-fetch from the server
  const handleServerSidePageChange = (newPageIndex) => {
    setCurrentIndex(newPageIndex + 1);
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor('orderId', {
        header: t('Order ID'),
        cell: (info) => {
          const orderId = info.getValue();
          return <Link to={`/orders/${orderId}`}>{orderId}</Link>;
        },
        key: 'orderId',
        filter: true,
        filterComponent: ({ value, onChange }) => (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t("Search order...")}
            style={{ width: '100%', padding: '4px' }}
          />
        ),
      }),
      columnHelper.accessor('organizationIdList', {
        cell: (info) => info?.row?.original?.billToPartyName || '',
        header: t('Organization'),
        filter: true,
        filterComponent: ({ value = [], onChange }) => {
          const handleCheckboxChange = (org) => {
            if (value.includes(org)) {
              onChange(value.filter((v) => v !== org));
            } else {
              onChange([...value, org]);
            }
          };

          return (
            <div className="sidebar-widget">
              <div className="sidebar-widget-list mt-10">
                <ul>
                  {organizations?.map((org) => (
                    <li key={org?.partyId}>
                      <label className="sidebar-widget-list-left">
                        <input
                          type="checkbox"
                          className={
                            value.includes(org?.partyId) ? 'active' : ''
                          }
                          onChange={() => handleCheckboxChange(org?.partyId)}
                        />
                        <span className="checkmark" />
                        <span className="checkmark-text">
                          {org?.billToPartyName || ''}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('orderEntryDates', {
        header: t('Placed Date'),
        cell: (info) => {
          const date = info?.row?.original?.orderDate || '';
          return formatDate(date);
        },
        filter: true,
        // Inline date range filter
        filterComponent: ({ value = {}, onChange }) => {
          const { startDate = '', endDate = '' } = value;

          const handleChange = (key, newVal) => {
            const updated = { ...value, [key]: newVal };
            onChange(updated);
          };

          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <label>
                {t("Start Date")}:
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  style={{ marginLeft: '8px' }}
                />
              </label>

              <label>
                {t("End Date")}:
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  style={{ marginLeft: '8px' }}
                />
              </label>
            </div>
          );
        },
      }),
      columnHelper.accessor('statusIdList', {
        header: t('Status'),
        cell: (info) =>
          getStatusDescription(info?.row?.original?.statusId || ''),
        filter: true,
        filterDefaultValue: () => '',
        filterComponent: ({ value = '', onChange }) => {
          const handleRadioChange = (statusId) => {
            if (value === statusId) {
              onChange('');
            } else {
              onChange(statusId);
            }
          };
          return (
            <div className="sidebar-widget">
              <div className="sidebar-widget-list">
                <ul>
                  {orderStatus?.map((st) => (
                    <li key={st?.statusId}>
                      <label className="sidebar-widget-list-left">
                        <input
                          type="radio"
                          className={value === st?.statusId ? 'active' : ''}
                          checked={value === st?.statusId}
                          onChange={() => handleRadioChange(st?.statusId)}
                        />
                        <span className="checkmark" />
                        <span className="checkmark-text">
                          {st?.description}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizations, orderStatus, i18n?.language]
  );

  const getStatusDescription = useCallback(
    (statusId) => {
      return (
        orderStatus?.find((st) => st?.statusId === statusId)?.description || ''
      );
    },
    [orderStatus]
  );

  return (
    <Fragment>
      <SEO titleTemplate={t("Orders")} description={t("Orders.")} />
      <LayoutOne
        headerContainerClass="container-fluid"
        headerPaddingClass="header-padding-2"
      >
        <Breadcrumb
          pages={[
            { label: t('Home'), path: process.env.PUBLIC_URL + '/' },
            { label: t('Orders'), path: process.env.PUBLIC_URL + pathname },
          ]}
        />
        <div className="contact-area pt-50 pb-100">
          <div className="container">
            <Table
              data={agentOrders || []}
              columns={columns}
              searchPlaceholder={t("Search orders...")}
              isLoading={isLoading}
              enableGlobalFilter={false}
              serverSideSorting={true}
              onServerSideSortChange={handleServerSideSortChange}
              serverSideFiltering={true}
              onServerSideFilterChange={handleServerSideFilterChange}
              totalRecords={totalRecords}
              initialPageIndex={currentIndex}
              onServerSidePageChange={handleServerSidePageChange}
              currentPage={currentIndex}
            />
          </div>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

export default Orders;
